import { z } from 'zod';
import { Device, DeviceAlert, DeviceCommand, DeviceHistory, Printer } from '@cloudsign/shared';
import type { DeviceAlertKind } from '@cloudsign/shared';
import { t } from '../../lib/i18n';

export const DeviceList = z.array(Device);
export const PrinterList = z.array(Printer);
export const AlertList = z.array(DeviceAlert);
export const CommandList = z.array(DeviceCommand);
export { DeviceHistory };

export const BASE = '/kiosk-devices';

/** Query-key roots; live updates invalidate the whole 'devices' root. */
export const keys = {
  list: (siteId: string) => ['devices', 'list', siteId] as const,
  detail: (id: string) => ['devices', 'detail', id] as const,
  alerts: (id: string) => ['devices', 'alerts', id] as const,
  commands: (id: string) => ['devices', 'commands', id] as const,
  history: (id: string, days: number) => ['devices', 'history', id, days] as const,
  printers: (siteId: string) => ['devices', 'printers', siteId] as const,
};

export const DEVICE_KINDS = ['kiosk', 'display', 'companion'] as const;
export const PRINTER_MODELS = ['brother_ql_820nwb', 'brother_ql_other'] as const;
export const PRINTER_MEDIA = ['dk11204_17x54', 'dk11209_29x62', 'dk11202_62x100', 'dk22205_62x90'] as const;

export function alertLabel(kind: DeviceAlertKind): string {
  return t(`devicesUi.alertKind.${kind}`);
}

/** True when the device is up but something needs a look (open alert or an update to install). */
export function needsAttention(d: Device): boolean {
  return d.openAlerts.length > 0 || d.updateAvailable;
}

export function batteryText(d: Device): string {
  const h = d.health;
  if (!h || h.batteryPercent === null) return t('devicesUi.battery.unknown');
  return t(h.charging ? 'devicesUi.battery.charging' : 'devicesUi.battery.value', { percent: h.batteryPercent });
}

export function sortValue(d: Device, field: string): string | number {
  switch (field) {
    case 'site':
      return d.siteId;
    case 'state':
      return d.online ? 1 : 0;
    case 'battery':
      return d.health?.batteryPercent ?? -1;
    case 'version':
      return d.appVersion ?? '';
    case 'lastSeen':
      return d.lastSeenAt ?? '';
    default:
      return d.name.toLowerCase();
  }
}
